import { computed, nextTick, onMounted, onScopeDispose, ref, toValue, watch } from 'vue';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { isBoolean } from 'lodash';

import { MAX_LOAD_TYPES_PER_WAREHOUSE, type Maybe } from '@nova/core';
import { useEventHub } from '@satellite/composables/useEventHub';
import { DEFAULT_INVALIDATE_OPTIONS, queryKeys } from '@satellite/composables/queryKeys';
import loadTypeService from '@satellite/services/loadtype-service';

import type { MaybeRefOrGetter } from 'vue';
import type { LoadTypeDetails } from '@satellite/composables/loadTypePresenterShared';

/**
 * A warehouse caps at `MAX_LOAD_TYPES_PER_WAREHOUSE` load types and the org list is
 * capped the same way, so one page holds both. The legacy select asked for a single
 * cap's worth and silently truncated once the two sets together exceeded it.
 */
const LOAD_TYPE_FETCH_LIMIT = MAX_LOAD_TYPES_PER_WAREHOUSE * 2;
const LOAD_TYPE_TOPICS = ['create-LoadType', 'update-LoadType', 'delete-LoadType'] as const;

interface LoadTypeEvent {
  warehouseId?: Maybe<string>;
}

export interface UseLoadTypeSelectListOptions {
  /** Required scope. The query stays disabled until this resolves to a non-empty id. */
  warehouseId: MaybeRefOrGetter<Maybe<string>>;
  /** Include org-level load types, which every warehouse can use. Defaults to true. */
  includeOrgLoadTypes?: MaybeRefOrGetter<boolean>;
  /** Restrict to load types assigned to at least one of the warehouse's docks. */
  showOnlyAssignedLoadTypes?: MaybeRefOrGetter<boolean>;
  /** Kept in `data` when the fetched page lacks it, so the current value never blanks. */
  selectedLoadType?: MaybeRefOrGetter<Maybe<LoadTypeDetails>>;
}

/**
 * Load types for one warehouse, fetched whole and filtered by the consuming select.
 *
 * The list is small and bounded, so there is no server-side search: one query per
 * warehouse/scope feeds every mounted select through the shared TanStack cache, and
 * EventHub invalidation keeps it live.
 */
export function useLoadTypeSelectList({
  warehouseId,
  includeOrgLoadTypes,
  showOnlyAssignedLoadTypes,
  selectedLoadType
}: UseLoadTypeSelectListOptions) {
  const eventHub = useEventHub();
  const queryClient = useQueryClient();
  const isInViewport = ref(false);
  const pendingInvalidation = ref(false);

  /**
   * Options stay `MaybeRefOrGetter` and are read inside computeds — destructuring
   * their values here would snapshot them and break reactivity.
   */
  const effectiveWarehouseId = computed(() => toValue(warehouseId) ?? null);
  const effectiveIncludeOrgLoadTypes = computed(() => toValue(includeOrgLoadTypes) ?? true);
  const effectiveShowOnlyAssignedLoadTypes = computed(
    () => toValue(showOnlyAssignedLoadTypes) ?? false
  );

  const loadTypeListQueryKey = computed(() =>
    queryKeys.loadTypeList(effectiveWarehouseId.value, {
      includeOrgLoadTypes: effectiveIncludeOrgLoadTypes.value,
      showOnlyAssignedLoadTypes: effectiveShowOnlyAssignedLoadTypes.value
    })
  );

  const loadTypeListQuery = useQuery({
    queryKey: loadTypeListQueryKey,
    queryFn: async ({ signal }) => {
      const currentWarehouseId = effectiveWarehouseId.value;

      // `refetch()` bypasses `enabled`, so the scope is re-checked here.
      if (!currentWarehouseId) {
        return [];
      }

      const response = await loadTypeService.getLoadtypesV2({
        requestConfig: {
          signal,
          params: {
            warehouseId: currentWarehouseId,
            includeOrgLoadTypes: effectiveIncludeOrgLoadTypes.value,
            showOnlyAssignedLoadTypes: effectiveShowOnlyAssignedLoadTypes.value,
            /** Rows only show name, direction and duration, so skip the server-side hierarchy merge. */
            includeHierarchySettings: false,
            limit: LOAD_TYPE_FETCH_LIMIT,
            sort: 'name,ASC'
          }
        }
      });

      return Array.isArray(response) ? (response as LoadTypeDetails[]) : [];
    },
    enabled: computed(() => isInViewport.value && Boolean(effectiveWarehouseId.value)),
    staleTime: Infinity,
    refetchOnWindowFocus: false
  });

  const data = computed<LoadTypeDetails[]>(() => {
    const loadTypes = (loadTypeListQuery.data.value as LoadTypeDetails[] | undefined) ?? [];
    const currentLoadType = selectedLoadType ? toValue(selectedLoadType) : null;

    if (!currentLoadType?.id || loadTypes.some(loadType => loadType.id === currentLoadType.id)) {
      return loadTypes;
    }

    /** Prepended so the consumer's grouping still files it under the right header. */
    return [currentLoadType, ...loadTypes];
  });

  const invalidate = () => {
    /** Every scope: an event can move a load type between the assigned and unassigned sets. */
    queryClient.invalidateQueries(
      {
        queryKey: queryKeys.loadTypeLists()
      },
      DEFAULT_INVALIDATE_OPTIONS
    );
  };

  const refresh = async () => {
    await nextTick();
    await loadTypeListQuery.refetch();
  };

  const deferOrInvalidate = () => {
    if (!isInViewport.value) {
      pendingInvalidation.value = true;
      return;
    }

    invalidate();
  };

  const onIntersect = (entry?: { isIntersecting?: boolean } | boolean) => {
    isInViewport.value = isBoolean(entry) ? entry : (entry?.isIntersecting ?? true);

    if (isInViewport.value && pendingInvalidation.value) {
      pendingInvalidation.value = false;
      invalidate();
    }
  };

  /**
   * Org load types carry no `warehouseId` and show up in every warehouse's list, so
   * they always invalidate. Warehouse-scoped events only matter for our warehouse.
   */
  const handleLoadTypeEvent = (event?: LoadTypeEvent | null) => {
    if (event?.warehouseId && event.warehouseId !== effectiveWarehouseId.value) {
      return;
    }

    deferOrInvalidate();
  };

  watch(
    [effectiveWarehouseId, effectiveIncludeOrgLoadTypes, effectiveShowOnlyAssignedLoadTypes],
    () => {
      pendingInvalidation.value = false;
    }
  );

  onMounted(() => {
    LOAD_TYPE_TOPICS.forEach(topic => eventHub.on(topic, handleLoadTypeEvent));
  });

  onScopeDispose(() => {
    LOAD_TYPE_TOPICS.forEach(topic => eventHub.off(topic, handleLoadTypeEvent));
  });

  return {
    data,
    error: computed(() => loadTypeListQuery.error.value),
    invalidate,
    isLoading: computed(() => loadTypeListQuery.isFetching.value),
    onIntersect,
    refresh
  };
}
